import { useRouter } from '@tanstack/react-router'
import { useEffect, useId, useState, type SyntheticEvent } from 'react'
import { useAuthStore } from '../auth/AuthProvider'
import { isAuthError } from '../auth/client'
import { AuthBanner, AuthCard, AuthTitle } from './AuthCard'
import { PasswordField } from './PasswordField'
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, SET_PASSWORD_COPY } from './SetPasswordPage'

export const RESET_PASSWORD_COPY = {
  title: 'Reset password',
  intro: 'Choose a new password for {email}.',
  checking: 'Checking your link…',
  expired: 'This link has expired',
  expiredIntro: 'Reset links work once and only for a short time. Ask for a new one.',
  sendNew: 'Send a new link',
  sameAsEmail: "Don't use your email address as your password.",
  submit: 'Set new password',
} as const

/** Firebase codes for a link that can't be used (expired, already used, malformed or for another project). */
const DEAD_LINK = new Set([
  'auth/expired-action-code',
  'auth/invalid-action-code',
  'auth/user-disabled',
  'auth/user-not-found',
])

/**
 * Where A-03 goes after success (Frontend §5.2): `continueUrl` only when its origin is one of the staff apps';
 * otherwise this app's A-01 with "Password updated". `null` for a same-origin target means "use the router".
 */
export function resetTarget(
  continueUrl: string | undefined,
  allowedOrigins: readonly string[],
  here: string = window.location.origin,
): { href: string; external: boolean } {
  const fallback = { href: '/sign-in?reason=password-updated', external: false }
  if (!continueUrl) return fallback
  let url: URL
  try {
    url = new URL(continueUrl)
  } catch {
    return fallback
  }
  if (url.origin === here) return { href: `${url.pathname}${url.search}${url.hash}`, external: false }
  return allowedOrigins.includes(url.origin) ? { href: url.href, external: true } : fallback
}

interface FieldErrors {
  password?: string
  confirm?: string
}

export interface ResetPasswordPageProps {
  siteName: string
  mode?: string | undefined
  oobCode?: string | undefined
  continueUrl?: string | undefined
  /** The two staff app origins (this one and the other), the only allowed `continueUrl` targets. */
  allowedOrigins: readonly string[]
}

/** A-03 Reset password, opened from Firebase's email (UserFlows A-03, Frontend §5.2, Security.md §10.2). */
export function ResetPasswordPage({ siteName, mode, oobCode, continueUrl, allowedOrigins }: ResetPasswordPageProps) {
  const store = useAuthStore()
  const router = useRouter()
  const id = useId()
  const usable = mode === 'resetPassword' && !!oobCode
  const [email, setEmail] = useState<string | null>(null)
  const [dead, setDead] = useState(!usable)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [errors, setErrors] = useState<FieldErrors>({})
  const [failed, setFailed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(0)

  useEffect(() => {
    if (!usable) return
    let live = true
    store.verifyPasswordResetCode(oobCode).then(
      (address) => {
        if (live) setEmail(address)
      },
      () => {
        if (live) setDead(true)
      },
    )
    return () => {
      live = false
    }
  }, [store, usable, oobCode])

  function validate(): FieldErrors {
    const next: FieldErrors = {}
    if (password.length < PASSWORD_MIN_LENGTH) next.password = SET_PASSWORD_COPY.tooShort
    else if (password.length > PASSWORD_MAX_LENGTH) next.password = SET_PASSWORD_COPY.tooWeak
    else if (password.trim().toLowerCase() === email?.toLowerCase()) next.password = RESET_PASSWORD_COPY.sameAsEmail
    if (confirm !== password) next.confirm = SET_PASSWORD_COPY.mismatch
    return next
  }

  async function submit(e: SyntheticEvent) {
    e.preventDefault()
    if (!oobCode) return
    setFailed(false)
    const found = validate()
    setErrors(found)
    if (found.password ?? found.confirm) return
    setBusy(true)
    try {
      await store.confirmPasswordReset(oobCode, password)
    } catch (err) {
      const code = isAuthError(err) ? err.code : ''
      if (DEAD_LINK.has(code)) setDead(true)
      else if (code === 'auth/weak-password' || code === 'auth/password-does-not-meet-requirements') {
        setErrors({ password: SET_PASSWORD_COPY.tooWeak })
      } else setFailed(true)
      setBusy(false)
      return
    }
    setDone((n) => n + 1)
    const target = resetTarget(continueUrl, allowedOrigins)
    if (target.external) window.location.assign(target.href)
    else await router.navigate({ href: target.href, replace: true })
  }

  if (dead) {
    return (
      <AuthCard siteName={siteName}>
        <div>
          <AuthTitle>{RESET_PASSWORD_COPY.expired}</AuthTitle>
          <p className="muted">{RESET_PASSWORD_COPY.expiredIntro}</p>
        </div>
        <button
          type="button"
          className="btn btn-primary btn-block"
          onClick={() => void router.navigate({ href: '/forgot-password' })}
        >
          {RESET_PASSWORD_COPY.sendNew}
        </button>
      </AuthCard>
    )
  }

  if (email === null) {
    return (
      <AuthCard siteName={siteName}>
        <div aria-busy="true">
          <AuthTitle>{RESET_PASSWORD_COPY.title}</AuthTitle>
          <p className="muted">{RESET_PASSWORD_COPY.checking}</p>
        </div>
      </AuthCard>
    )
  }

  const describe = (field: keyof FieldErrors) => (errors[field] ? `${id}-${field}-error` : undefined)
  return (
    <AuthCard siteName={siteName}>
      <div>
        <AuthTitle>{RESET_PASSWORD_COPY.title}</AuthTitle>
        <p className="muted">{RESET_PASSWORD_COPY.intro.replace('{email}', email)}</p>
      </div>
      {failed && (
        <AuthBanner kind="error">Couldn&apos;t save the password. Check your connection and try again.</AuthBanner>
      )}
      <form onSubmit={(e) => void submit(e)} noValidate>
        <div className="field">
          <label className="label" htmlFor={`${id}-password`}>
            New password
          </label>
          <PasswordField
            id={`${id}-password`}
            autoComplete="new-password"
            resetKey={done}
            required
            aria-required
            aria-invalid={errors.password ? true : undefined}
            aria-describedby={describe('password') ?? `${id}-password-hint`}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
            }}
          />
          {errors.password ? (
            <span className="err-text" id={`${id}-password-error`}>
              {errors.password}
            </span>
          ) : (
            <span className="hint" id={`${id}-password-hint`}>
              At least {PASSWORD_MIN_LENGTH} characters.
            </span>
          )}
        </div>
        <div className="field">
          <label className="label" htmlFor={`${id}-confirm`}>
            Confirm new password
          </label>
          <PasswordField
            id={`${id}-confirm`}
            autoComplete="new-password"
            resetKey={done}
            required
            aria-required
            aria-invalid={errors.confirm ? true : undefined}
            aria-describedby={describe('confirm')}
            value={confirm}
            onChange={(e) => {
              setConfirm(e.target.value)
            }}
          />
          {errors.confirm && (
            <span className="err-text" id={`${id}-confirm-error`}>
              {errors.confirm}
            </span>
          )}
        </div>
        <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
          {busy ? 'Saving…' : RESET_PASSWORD_COPY.submit}
        </button>
      </form>
    </AuthCard>
  )
}
